import type { ProductPublicationStatus, ProductStockStatus, Currency } from '../../../constants';
import type { Product, TaxonomyTerm, ProductDetailData, ProductMeta, SimpleProduct, ProductInfo } from '../../../model';

export type ProductsCollectionRequest = {
  collectionName: string;
  page?: number;
  limit?: number;
  publicationStatus?: ProductPublicationStatus;
  stockStatus?: ProductStockStatus;
  currency: Currency;
  categories?: string;
};

export type ProductsCollectionResponse = {
  products: Product[];
  total: number;
  page: number;
  limit: number;
};

export type ProductsAllRequest = {
  page?: number;
  limit?: number;
  publicationStatus?: ProductPublicationStatus;
  stockStatus?: ProductStockStatus;
  currency: Currency;
};

export type ProductsAllResponse = {
  products: Product[];
  total: number;
  page: number;
  limit: number;
};

export type ProductsByIdsRequest = {
  ids: string;
  currency: Currency;
  locale?: string;
};

export type ProductsDetailRequest = {
  /**
   * Product Slug (e.g., "snow-flower-whipping-cleanser-150ml-1ea")
   */
  slug: string;
  currency: Currency;
  locale?: string;
};

export type ProductMetaRequest = {
  /**
   * Product ID (e.g., "123")
   */
  productId: string;
  locale?: string;
};

export type ProductsByIdsResponse = Product[];
export type ProductsDetailResponse = ProductDetailData;
export type ProductMetaResponse = ProductMeta;

export type TopRankedProductsRequest = {
  collectionName?: string;
  page?: number;
  limit?: number;
  currency: Currency;
  categories?: string;
};

export type TopRankedProductsResponse = {
  products: Product[];
};

export type ProductsCollectionCompetitorRequest = {
  competitorName: string;
  collectionName?: string;
  page?: number;
  limit?: number;
  publicationStatus?: ProductPublicationStatus;
  stockStatus?: ProductStockStatus;
  currency?: Currency;
  categories?: string;
};

export type ProductsCollectionCompetitorResponse = {
  products: Product[];
};

export type ProductTaxonomy = {
  skinType: TaxonomyTerm[];
  skinConcern: TaxonomyTerm[];
  ingredient: TaxonomyTerm[];
};

export type ProductBrandTaxonomy = TaxonomyTerm[];

export type CategoryTaxonomy = TaxonomyTerm[];

export type ProductBrowseRequest = {
  publicationStatus?: 'publish' | 'draft';
  stockStatus?: 'instock' | 'outofstock';
  currency: Currency;
  categories?: string;
  brands?: string;
  skinTypes?: string;
  skinConcerns?: string;
  ingredients?: string;
  productTagSlugs?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  minPrice?: number;
  maxPrice?: number;
};

export type FilterSummaryItem = {
  termId: number;
  name: string;
  count: number;
};

export type FilterSummary = {
  brand: FilterSummaryItem[];
  skinType: FilterSummaryItem[];
  skinConcern: FilterSummaryItem[];
  ingredient: FilterSummaryItem[];
  priceRange: {
    min: number;
    max: number;
  };
};

export type ProductBrowseResponse = {
  products: Product[];
  total: number;
  page: number;
  limit: number;
  summary: FilterSummary;
};

export type ProductSearchRequest = ProductBrowseRequest & {
  q: string;
};

export type ProductSearchResponse = ProductBrowseResponse & {
  searchQuery: string;
};

export type ListRecommendationsRequest = {
  productId: number;
  limit?: number;
  filter?: 'moreReviews' | 'lowerPrice';
  currency?: Currency;
};
export type QueryListRecommendationsRequest = Partial<ListRecommendationsRequest>;

export type RecommendedProduct = ProductInfo & {
  efficacy: string[];
  unitPrice: {
    label: string;
    value: number;
  };
  parentProductId: number;
  popularity: number;
  score: number;
};

export type ListRecommendationsResponse = {
  original: RecommendedProduct;
  recommendations: RecommendedProduct[];
};

export type ProductsRecommendRequest = {
  recommendName: string;
  productIds?: string;
  actorId?: string;
  limit?: number;
  currency: Currency;
};

export type ProductsRecommendResponse = {
  products: Product[];
  total: number;
  page: number;
  limit: number;
};

export type AIInsightRequest = {
  orgProductId: number;
  altProductId: number;
};

export type AIInsightResponse = {
  original: RecommendedProduct;
  recommendations: RecommendedProduct[];
  insight: string;
};
